import { previousMonday } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { GetMonthNameEnglish } from '../SchedulelistCommon';
import { ChartType } from './GanttChartArea';

interface Props {
  /** チャート種別（Month/Week/Day） */
  chartType: ChartType;

  dateList: Date[];
}

/** ヘッダー部分格納用オブジェクト */
type Thead = { headRow1: string; headRow2: string };

/**
 *ガントチャートヘッダー部分の描画
 */
function GanttChartTheadder({ chartType, dateList }: Props) {
  const {
    i18n: { language },
  } = useTranslation();

  // 対象期間リストからヘッダー情報生成
  const thList = convertThList(dateList, language);

  // ヘッダーの表示
  return <TheadderComponent thList={thList} chartType={chartType} />;
}

interface TheadderRowProps {
  thList: { monthlist: Thead[]; weeklist: Thead[]; daylist: Thead[] };
  chartType: ChartType;
}

/**
 * ガントチャートヘッダー部分コンポーネント
 * @param thList
 */
function TheadderComponent({ thList, chartType }: TheadderRowProps) {
  // 言語情報
  const { t } = useTranslation();

  // チャート種別に応じてデータ切り替え
  let dateList = [];
  switch (chartType) {
    case 'Month':
      dateList = thList.monthlist;
      break;
    case 'Week':
      dateList = thList.weeklist;
      break;
    case 'Day':
      dateList = thList.daylist;
      break;
    default:
      dateList = thList.monthlist;
      break;
  }

  // 対象期間のヘッダー情報リストからヘッダーの各部分を作成
  const [thList1, thList2] = ConvertTheadderTrow(dateList);

  return (
    <div className="thead23">
      <div className="tr23">
        <div className="th23 sticky">{}</div>
        <div className="th23 sticky">{}</div>
        <div className="th23 sticky">{}</div>
        {thList1}
      </div>
      <div className="tr23">
        <div className="th23 sticky">{t('資料管理No')}</div>
        <div className="th23 sticky">{t('機種名')}</div>
        <div className="th23 sticky">{t('M/Y')}</div>
        {thList2}
      </div>
    </div>
  );
}

/**
 * 対象期間のヘッダー情報リストからヘッダーの各部分を作成
 * @param dateList
 * @returns
 */
function ConvertTheadderTrow(dateList: Thead[]) {
  // ガントチャートヘッダー上部のth要素を作成
  const thList1 = dateList.map((rowInfo, i) => (
    <div key={rowInfo.headRow1 + i.toString()} className={`th23 ${rowInfo.headRow1 !== '' ? 'l-line' : ''}`}>
      {rowInfo.headRow1}
    </div>
  ));

  // ガントチャートヘッダー下部のth要素を作成
  const thList2 = dateList.map((rowInfo, i) => (
    <div key={rowInfo.headRow1 + rowInfo.headRow2 + i.toString()} className="th23">
      {rowInfo.headRow2}
    </div>
  ));
  return [thList1, thList2];
}

/**
 * 期間リストをチャート種別ごとのTheadオブジェクトの配列に変換
 * @param dateList 期間リスト（中抜けなし）
 * @returns
 */
function convertThList(dateList: Date[], language: string) {
  const monthlist: Thead[] = [];
  /** Month表示 1行目:year */
  let tempMonthRow1: number;
  /** Month表示 2行目:month */
  let tempMonthRow2: number;
  let tempMonth: string;

  const weeklist: Thead[] = [];
  /** Week表示 1行目:year＋month */
  let tempWeekRow1: number;
  /** Week表示 2行目:何週目 */
  let tempWeekRow2: number;
  let tempWeek: string;

  const daylist: Thead[] = [];
  /** Day表示 1行目:year＋month */
  let tempDayRow1: number;

  dateList.forEach((date) => {
    // 対象年
    const year = date.getFullYear();
    // 対象月
    const month = date.getMonth() + 1;
    // 対象週（月の何週目かを取得）
    const week = getWeeks(date);
    // 対象年（週設定の場合）
    const yearOfWeek = getMonthByYear(date);
    // 対象月（週設定の場合）
    const monthOfWeek = getMonthByWeek(date);

    // チャート種別[Month]の際に表示するヘッダー情報の格納
    let monthListChangeFlg = false;
    if (tempMonthRow1 !== year) {
      tempMonthRow1 = year;
      monthListChangeFlg = true;
    }
    if (tempMonthRow2 !== month) {
      tempMonthRow2 = month;
      monthListChangeFlg = true;
    }
    if (monthListChangeFlg) {
      let theadMonthStr = '';
      let monthRow = `${tempMonthRow1.toString()}年`;
      let monthRow2 = `${tempMonthRow2.toString()}月`;
      if (language !== 'ja') {
        monthRow = tempMonthRow1.toString();
        monthRow2 = GetMonthNameEnglish(tempMonthRow2);
      }
      if (tempMonth !== monthRow) {
        tempMonth = monthRow;
        theadMonthStr = tempMonth;
      } else {
        theadMonthStr = '';
      }

      const thead: Thead = { headRow1: theadMonthStr, headRow2: monthRow2 };
      monthlist.push(thead);
    }

    // チャート種別[Week]の際に表示するヘッダー情報の格納
    let weekListChangeFlg = false;
    if (tempWeekRow1 !== monthOfWeek) {
      tempWeekRow1 = monthOfWeek;
      weekListChangeFlg = true;
    }
    if (tempWeekRow2 !== week) {
      tempWeekRow2 = week;
      weekListChangeFlg = true;
    }
    if (weekListChangeFlg) {
      let theadWeekStr = '';
      let weekRow1 = `${yearOfWeek.toString()}年${tempWeekRow1.toString()}月`;
      let weekRow2 = `${tempWeekRow2.toString()}週`;
      if (language !== 'ja') {
        weekRow1 = `${GetMonthNameEnglish(tempWeekRow1)} ${yearOfWeek.toString()}`;
        weekRow2 = `Week ${tempWeekRow2.toString()}`;
      }
      if (tempWeek !== weekRow1) {
        tempWeek = weekRow1;
        theadWeekStr = weekRow1;
      } else {
        theadWeekStr = '';
      }
      const thead: Thead = {
        headRow1: theadWeekStr,
        headRow2: weekRow2,
      };
      weeklist.push(thead);
    }

    // チャート種別[Day]の際に表示するヘッダー情報の格納
    let dayRow1 = '';
    let dayRow2 = `${date.getDate()}日`;
    if (tempDayRow1 !== month) {
      tempDayRow1 = month;
      dayRow1 = `${year.toString()}年${month.toString()}月`;
      if (language !== 'ja') {
        dayRow1 = `${GetMonthNameEnglish(month)} ${year.toString()}`;
      }
    }
    if (language !== 'ja') {
      dayRow2 = date.getDate().toString();
    }
    const DThead: Thead = {
      headRow1: dayRow1,
      headRow2: dayRow2,
    };
    daylist.push(DThead);
  });
  return { monthlist, weeklist, daylist };
}

/**
 * 現在の週情報取得（月曜日はじめ）
 * @param chkdate
 * @returns
 */
function getWeeks(chkdate: Date) {
  const targetDate = new Date(chkdate.getFullYear(), chkdate.getMonth(), chkdate.getDate());
  // 月曜日の場合
  if (chkdate.getDay() === 1) {
    targetDate.setDate(targetDate.getDate() + 1);
  }
  const preMonday = previousMonday(targetDate);
  return Math.floor((preMonday.getDate() + 6) / 7);
}

/**
 * 月曜日はじめの場合の週に対応する月情報取得
 * @param chkdate
 * @returns
 */
function getMonthByWeek(chkdate: Date) {
  const targetDate = new Date(chkdate.getFullYear(), chkdate.getMonth(), chkdate.getDate());
  // 月曜日の場合
  if (chkdate.getDay() === 1) {
    targetDate.setDate(targetDate.getDate() + 1);
  }
  const preMonday = previousMonday(targetDate);
  return preMonday.getMonth() + 1;
}

/**
 * 月曜日はじめの場合の週に対応する年情報取得
 * @param chkdate
 * @returns
 */
function getMonthByYear(chkdate: Date) {
  const targetDate = new Date(chkdate.getFullYear(), chkdate.getMonth(), chkdate.getDate());
  // 月曜日の場合
  if (chkdate.getDay() === 1) {
    targetDate.setDate(targetDate.getDate() + 1);
  }
  const preMonday = previousMonday(targetDate);
  return preMonday.getFullYear();
}

export default GanttChartTheadder;
